/**
 * AppShell
 *
 * Permanent sidebar layout (desktop).
 * - Sidebar is navigation only (never lists risks/objects).
 * - Main content renders the active route via <Outlet/>.
 *
 * Branding:
 * - Uses Cyberlope logo and theme colors derived from the logo.
 */

import React, { useCallback } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Snackbar,
  Toolbar,
  Typography,
  Button,
  Switch,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useAuth } from "./AuthProvider";
import { registerGlobalNoticeHandler } from "../services/session";
import logoUrl from "../assets/cyberlope-logo.svg";
import iconUrl from "../assets/cyberlope-icon.svg";
import traLogoUrl from "../assets/tra-logo.svg";
import PersonIcon from "@mui/icons-material/Person";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ListAltIcon from "@mui/icons-material/ListAlt";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ShieldIcon from "@mui/icons-material/Shield";
import ArticleIcon from "@mui/icons-material/Article";
import CategoryIcon from "@mui/icons-material/Category";
import SecurityIcon from "@mui/icons-material/Security";
import TimelineIcon from "@mui/icons-material/Timeline";
import PeopleIcon from "@mui/icons-material/People";
import LockIcon from "@mui/icons-material/Lock";
import GppMaybeIcon from "@mui/icons-material/GppMaybe";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import { useTaskListStats } from "../hooks/useTaskListStats";
import { useUnsavedNavigationGuardContext } from "../contexts/UnsavedNavigationGuardContext";
import { useAssessment } from "./AssessmentProvider";
import { WorkspaceRecentsMenu } from "./WorkspaceRecentsMenu";
import { useThemeMode } from "../contexts/ThemeModeContext";
import { useRuntimeConfig } from "../contexts/RuntimeConfigContext";

const drawerWidth = 260;
const collapsedWidth = 72;

export function AppShell() {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("md"));
  const [drawerOpen, setDrawerOpen] = React.useState(true);
  React.useEffect(() => {
    if (isSmDown) {
      setDrawerOpen(true);
    }
  }, [isSmDown]);
  const { username, fullName, roles, logout, isReady } = useAuth();
  const [globalNotice, setGlobalNotice] = React.useState<string | null>(null);
  const { selected } = useAssessment();
  const { features } = useRuntimeConfig();
  const isAdmin = roles.includes("admin");
  const nav = useNavigate();
  const location = useLocation();
  const { mode, toggleMode } = useThemeMode();
  const displayName = fullName ?? username ?? "—";
  const loginCaption = username && username !== fullName ? username : null;
  const taskCount = useTaskListStats();
  const taskListEnabled = features.task_list !== false;
  const isArchitectureFrozen = Boolean(selected?.architecture_frozen);
  const showThreatModel =
    Boolean(features.threat_modeling) &&
    selected?.threat_modeling_enabled !== false;
  const compactNav = !drawerOpen && !isSmDown;
  const drawerDisplayWidth = drawerOpen ? drawerWidth : collapsedWidth;
  const [profileAnchor, setProfileAnchor] = React.useState<null | HTMLElement>(null);
  const [showRefreshSpinner, setShowRefreshSpinner] = React.useState(false);
  const { guard } = useUnsavedNavigationGuardContext();
  const guardNavigate = useCallback(
    (next: () => void) => {
      if (guard?.isDirty) {
        guard.requestNavigation(next);
        return;
      }
      next();
    },
    [guard]
  );

  React.useEffect(() => {
    const handler = (message: string) => setGlobalNotice(message);
    registerGlobalNoticeHandler(handler);
    return () => registerGlobalNoticeHandler(null);
  }, []);

  const backgroundText = React.useMemo(() => {
    if (location.pathname.startsWith("/assessments")) {
      return "All Risk Assessments";
    }
    if (location.pathname.startsWith("/workspace")) {
      return "Workspace";
    }
    if (
      location.pathname.startsWith("/admin/product-types") ||
      location.pathname.startsWith("/admin/security-levels") ||
      location.pathname.startsWith("/admin/scale-pairs") ||
      location.pathname.startsWith("/admin/users")
    ) {
      return "Administration";
    }
    if (location.pathname.startsWith("/profile")) {
      return "User Settings";
    }
    return "";
  }, [location.pathname]);
  const backgroundTextColor =
    theme.palette.mode === "dark" ? "rgba(255,255,255,0.18)" : "rgba(0, 0, 0, 0.26)";
  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setProfileAnchor(event.currentTarget);
  };
  const closeProfileMenu = () => {
    setProfileAnchor(null);
  };

  React.useEffect(() => {
    if (isReady) {
      setShowRefreshSpinner(false);
      return;
    }
    const t = window.setTimeout(() => setShowRefreshSpinner(true), 150);
    return () => window.clearTimeout(t);
  }, [isReady]);

  const avatarBgColor = "common.white";
  const avatarIconColor = theme.palette.mode === "dark" ? "#386a7a" : "#00B3D1";

  const avatarNode = (
    <Box sx={{ position: "relative", display: "inline-flex" }}>
      <Avatar
        onClick={handleAvatarClick}
        sx={{
          bgcolor: avatarBgColor,
          color: avatarIconColor,
          cursor: "pointer",
        }}
      >
        {isAdmin ? <VerifiedUserIcon fontSize="small" /> : <PersonIcon fontSize="small" />}
      </Avatar>
      {showRefreshSpinner ? (
        <Box
          sx={{
            position: "absolute",
            inset: -2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <CircularProgress size={24} thickness={5} sx={{ color: "#00B3D1" }} />
        </Box>
      ) : null}
    </Box>
  );
  const themeToggleLabel = mode === "dark" ? "Light mode" : "Dark mode";
  const themeToggleIcon = mode === "dark" ? <LightModeIcon /> : <DarkModeIcon />;
  const navBgColor = theme.palette.mode === "dark" ? "#386a7a" : "#00B3D1";

  return (
    <Box sx={{ display: "flex", height: "100vh", position: "relative" }}>
      <Box
        component="span"
        sx={{
          position: "fixed",
          inset: 0,
          pointerEvents: "none",
          zIndex: -1,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "flex-end",
          px: 4,
          pt: 4,
        }}
      >
        {backgroundText && backgroundText !== "Workspace" ? (
          <Typography
            variant="h4"
            sx={{
              color: backgroundTextColor,
              fontWeight: 700,
              fontStyle: "italic",
              textTransform: "none",
              letterSpacing: 1,
              lineHeight: 1,
            }}
          >
            {backgroundText}
          </Typography>
        ) : null}
      </Box>
      {backgroundText === "Workspace" ? (
        <Box
          component="span"
          sx={{
            position: "fixed",
            inset: 0,
            pointerEvents: "none",
            zIndex: 1,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "flex-end",
            px: 4,
            pt: 4,
          }}
        >
          <Box sx={{ pointerEvents: "auto" }}>
            <WorkspaceRecentsMenu />
          </Box>
        </Box>
      ) : null}
      <Drawer
        variant="permanent"
        sx={{
          width: drawerDisplayWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerDisplayWidth,
            boxSizing: "border-box",
            bgcolor: navBgColor,
            color: "common.white",
            overflowX: "hidden",
            transition: theme.transitions.create("width", { duration: 200 }),
          },
        }}
      >
        <Toolbar
          sx={{
            px: 2,
            py: 2,
            display: "flex",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <Box
            component="img"
            src={drawerOpen ? logoUrl : iconUrl}
            alt="Cyberlope"
            sx={{
              height: 52,
              width: drawerOpen ? "auto" : 52,
              objectFit: "contain",
              transition: theme.transitions.create(["width", "height"], { duration: 200 }),
              display: "block",
              mx: "auto",
            }}
          />
          <IconButton
            onClick={() => setDrawerOpen((prev) => !prev)}
            size="small"
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "common.white",
              bgcolor: "rgba(255,255,255,0.12)",
              transition: theme.transitions.create(["opacity", "background-color"], { duration: 200 }),
              opacity: 0,
              pointerEvents: "none",
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.24)",
              },
              ".MuiDrawer-paper:hover &": {
                opacity: 1,
                pointerEvents: "auto",
              },
            }}
          >
            {drawerOpen ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </IconButton>
        </Toolbar>
        <Divider sx={{ borderColor: "rgba(255,255,255,0.2)" }} />
        <List dense>
        <NavItem
          to="/assessments"
          label="All Risk Assessments"
          icon={<AssignmentIndIcon />}
          compact={compactNav}
          onNavigate={guardNavigate}
        />
        </List>
        <Divider sx={{ borderColor: "rgba(255,255,255,0.2)" }} />
        <Box sx={{ display: drawerOpen ? "block" : "none", px: 2, py: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: "rgba(255,255,255,0.8)" }}>
            Workspace
          </Typography>
        </Box>
        <List dense sx={{ gap: compactNav ? 1 : 0 }}>
          <NavItem
            to="/workspace/overview"
            label="Overview"
            currentPath={location.pathname}
            icon={<DashboardIcon />}
            compact={compactNav}
            onNavigate={guardNavigate}
          />
          {taskListEnabled ? (
            <NavItem
              to="/workspace/task-list"
              label="Task List"
              currentPath={location.pathname}
              icon={<ListAltIcon />}
              compact={compactNav}
              onNavigate={guardNavigate}
              action={
                taskCount != null ? (
                  <Chip
                    label={taskCount}
                    size="small"
                    variant="outlined"
                    sx={{ borderColor: "rgba(255,255,255,0.6)", color: "common.white" }}
                  />
                ) : null
              }
            />
          ) : null}
          <NavItem
            to="/workspace/objects"
            label="Architecture"
            currentPath={location.pathname}
            icon={<AccountTreeIcon />}
            action={
              isArchitectureFrozen ? <LockIcon fontSize="small" sx={{ color: "rgba(255,255,255,0.85)" }} /> : null
            }
            compact={compactNav}
            onNavigate={guardNavigate}
          />
          <NavItem
            to="/workspace/risks"
            label="Risks"
            currentPath={location.pathname}
            icon={<WarningAmberIcon />}
            compact={compactNav}
            onNavigate={guardNavigate}
          />
          <NavItem
            to="/workspace/mitigations"
            label="Mitigations"
            currentPath={location.pathname}
            icon={<ShieldIcon />}
            compact={compactNav}
            onNavigate={guardNavigate}
          />
          <NavItem
            to="/workspace/reports"
            label="Reports"
            currentPath={location.pathname}
            icon={<ArticleIcon />}
            compact={compactNav}
            onNavigate={guardNavigate}
          />
          {showThreatModel ? (
            <NavItem
              to="/workspace/threat-model"
              label="Threat Model"
              currentPath={location.pathname}
              icon={<GppMaybeIcon />}
              compact={compactNav}
              onNavigate={guardNavigate}
            />
          ) : null}
        </List>
        {isAdmin ? (
          <>
            <Divider />
            <List dense sx={{ gap: compactNav ? 1 : 0 }}>
              {compactNav ? (
                <Box aria-hidden="true" sx={{ height: 32 }} />
              ) : (
                <SectionTitle title="Administration" />
              )}
              <NavItem
                to="/admin/product-types"
                label="Component Types"
                icon={<CategoryIcon />}
                compact={compactNav}
                onNavigate={guardNavigate}
              />
              <NavItem
                to="/admin/security-levels"
                label="Frameworks"
                icon={<SecurityIcon />}
                compact={compactNav}
                onNavigate={guardNavigate}
              />
              <NavItem
                to="/admin/scale-pairs"
                label="Risk Scales"
                icon={<TimelineIcon />}
                compact={compactNav}
                onNavigate={guardNavigate}
              />
              <NavItem
                to="/admin/users"
                label="Users"
                icon={<PeopleIcon />}
                compact={compactNav}
                onNavigate={guardNavigate}
              />
            </List>
          </>
        ) : null}
          <Box sx={{ flex: 1 }} />
          <List dense>
            {compactNav ? (
              <Tooltip
                title={themeToggleLabel}
                placement="right"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      fontSize: "0.95rem",
                      bgcolor: "#00B3D1",
                      color: "common.white",
                      border: "1px solid #00B3D1",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                    },
                  },
                  arrow: {
                    sx: {
                      color: "#00B3D1",
                    },
                  },
                }}
              >
                <ListItemButton
                  onClick={toggleMode}
                  sx={{
                    color: "common.white",
                    "&:hover": {
                      bgcolor: "rgba(255,255,255,0.12)",
                    },
                    px: 1.5,
                    minHeight: 52,
                    py: 0.75,
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <ListItemIcon sx={{ color: "inherit", minWidth: 0 }}>
                    {themeToggleIcon}
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>
            ) : (
              <ListItemButton
                onClick={toggleMode}
                sx={{
                  color: "common.white",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.12)",
                  },
                  px: 1.5,
                  minHeight: 52,
                  py: 0.75,
                  justifyContent: "flex-start",
                  alignItems: "center",
                }}
              >
                <ListItemIcon
                  sx={{
                    color: "inherit",
                    minWidth: 36,
                    mx: 1,
                  }}
                >
                  <Switch
                    checked={mode === "dark"}
                    onChange={toggleMode}
                    onClick={(event) => event.stopPropagation()}
                    size="small"
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "common.white",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        bgcolor: "rgba(255,255,255,0.6)",
                      },
                      "& .MuiSwitch-track": {
                        bgcolor: "rgba(255,255,255,0.35)",
                        opacity: 1,
                      },
                    }}
                  />
                </ListItemIcon>
                <Box
                  sx={{
                    position: "relative",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <Typography sx={{ color: "inherit", fontWeight: 500, position: "relative", zIndex: 1 }}>
                    {themeToggleLabel}
                  </Typography>
                  <Box
                    aria-hidden="true"
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      gap: 0.75,
                      color: "rgba(255,255,255,0.7)",
                      opacity: 0.35,
                      pointerEvents: "none",
                    }}
                  >
                    <LightModeIcon fontSize="small" />
                    <DarkModeIcon fontSize="small" />
                  </Box>
                </Box>
              </ListItemButton>
            )}
          </List>
          <Divider sx={{ borderColor: "rgba(255,255,255,0.2)" }} />
          <Box sx={{ px: 2, py: 3 }}>
          <Stack spacing={1}>
            <Box
              sx={{
                minHeight: 72,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2} sx={{ width: "100%", justifyContent: compactNav ? "center" : "flex-start" }}>
                {compactNav ? (
                  <Tooltip
                    title="Profile"
                    arrow
                    placement="right"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          fontSize: "0.95rem",
                          bgcolor: "#00B3D1",
                          color: "common.white",
                          border: "1px solid #00B3D1",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                        },
                      },
                      arrow: {
                        sx: {
                          color: "#00B3D1",
                        },
                      },
                    }}
                  >
                    {avatarNode}
                  </Tooltip>
                ) : (
                  avatarNode
                )}
                {!compactNav && (
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", textTransform: "uppercase", letterSpacing: 0.5 }}>
                      Signed in as
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {displayName}
                    </Typography>
                    {loginCaption ? (
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
                        {loginCaption}
                      </Typography>
                    ) : null}
                  </Box>
                )}
              </Stack>
            </Box>
          </Stack>
          <Menu
            anchorEl={profileAnchor}
            open={Boolean(profileAnchor)}
            onClose={closeProfileMenu}
            PaperProps={{
              sx: { bgcolor: "background.paper", color: "text.primary" },
            }}
          >
            <MenuItem
              onClick={() => {
                guardNavigate(() => {
                  closeProfileMenu();
                  nav("/profile");
                });
              }}
            >
              Profile
            </MenuItem>
            <MenuItem
              onClick={() => {
                guardNavigate(() => {
                  closeProfileMenu();
                  logout();
                  nav("/login");
                });
              }}
            >
              Logout
            </MenuItem>
          </Menu>
          </Box>
        </Drawer>

      <Box component="main" sx={{ flexGrow: 1, bgcolor: "transparent", minWidth: 0 }}>
        <Toolbar />
        <Box sx={{ p: 2, height: "calc(100vh - 64px)", overflow: "hidden", minWidth: 0 }}>
          <Outlet />
        </Box>
      </Box>
      <Snackbar
        open={Boolean(globalNotice)}
        autoHideDuration={4000}
        onClose={() => setGlobalNotice(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="warning" onClose={() => setGlobalNotice(null)} sx={{ width: "100%" }}>
          {globalNotice}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function NavItem({
  to,
  label,
  disabled,
  currentPath,
  action,
  labelAdornment,
  icon,
  compact = false,
  onNavigate,
}: {
  to: string;
  label: string;
  disabled?: boolean;
  currentPath?: string;
  action?: React.ReactNode;
  labelAdornment?: React.ReactNode;
  icon?: React.ReactNode;
  compact?: boolean;
  onNavigate?: (next: () => void) => void;
}) {
  const navigate = useNavigate();
  const active = currentPath ? currentPath.startsWith(to) : false;

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) return;
    event.preventDefault();
    const performNavigation = () => {
      if (active) {
        navigate(to, { replace: true, state: { returnToRoot: true } });
        return;
      }
      navigate(to);
    };
    if (onNavigate) {
      onNavigate(performNavigation);
      return;
    }
    performNavigation();
  };

  const button = (
    <ListItemButton
      component={NavLink}
      to={to}
      disabled={disabled}
      onClick={handleClick}
      sx={{
        color: "common.white",
        "&.active": {
          bgcolor: "rgba(255,255,255,0.16)",
          borderLeft: 3,
          borderLeftColor: "common.white",
        },
        "&:hover": {
          bgcolor: "rgba(255,255,255,0.12)",
        },
        px: icon ? 1.5 : 3,
        minHeight: 52,
        py: compact ? 0.75 : 0.75,
        justifyContent: compact ? "center" : "flex-start",
        alignItems: "center",
      }}
    >
      {icon ? (
        <ListItemIcon
          sx={{
            color: "inherit",
            minWidth: compact ? 0 : 36,
            mx: compact ? 0 : 1,
          }}
        >
          {icon}
        </ListItemIcon>
      ) : null}
      {!compact ? (
        <ListItemText
          primary={
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography sx={{ color: "inherit", fontWeight: active ? 700 : 500 }}>{label}</Typography>
              {labelAdornment}
            </Stack>
          }
        />
      ) : null}
      {!compact && action ? (
        <Box sx={{ ml: "auto", display: "flex", alignItems: "center" }}>{action}</Box>
      ) : null}
    </ListItemButton>
  );

  if (compact) {
    return (
    <Tooltip
      title={label}
      placement="right"
      arrow
      componentsProps={{
                      tooltip: {
                        sx: {
                          fontSize: "0.95rem",
                          bgcolor: "#00B3D1",
                          color: "common.white",
                          border: "1px solid #00B3D1",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                        },
                      },
        arrow: {
          sx: {
            color: "#00B3D1",
          },
        },
      }}
    >
        {button}
      </Tooltip>
    );
  }

  return button;
}

function SectionTitle({ title }: { title: string }) {
  return (
    <Box sx={{ px: 2, py: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: "rgba(255,255,255,0.8)" }}>
        {title}
      </Typography>
    </Box>
  );
}
